import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { authAPI, tenantAPI } from "@/lib/api";
import AuthCallback from "@/components/AuthCallback";
import Sidebar from "@/components/Sidebar";
import QuickCreateButton from "@/components/QuickCreateButton";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProductsPage from "@/pages/ProductsPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import SuppliersPage from "@/pages/SuppliersPage";
import SupplierDetailPage from "@/pages/SupplierDetailPage";
import SupplierOrdersPage from "@/pages/SupplierOrdersPage";
import SupplierQuotesPage from "@/pages/SupplierQuotesPage";
import DocumentsPage from "@/pages/DocumentsPage";
import StatsPage from "@/pages/StatsPage";
import AdminPage from "@/pages/AdminPage";
import BillingForecastPage from "@/pages/BillingForecastPage";
import BankStatementPage from "@/pages/BankStatementPage";
import TenantSelectPage from "@/pages/TenantSelectPage";
import ImportPage from "@/pages/ImportPage";
import TvaPage from "@/pages/TvaPage";
import SupplierInvoicesPage from "@/pages/SupplierInvoicesPage";
import MarginAnalysisPage from "@/pages/MarginAnalysisPage";
import CampaignsPage from "@/pages/CampaignsPage";
import MailingListsPage from "@/pages/MailingListsPage";
import EmailTemplatesPage from "@/pages/EmailTemplatesPage";
import ExportPage from "@/pages/ExportPage";
import ComptabilitePage from "@/pages/ComptabilitePage";
import ExpenseNotesPage from "@/pages/ExpenseNotesPage";
import AIChatWidget from "@/components/AIChatWidget";
import IdleSessionManager from "@/components/IdleSessionManager";
import { useSidebarCollapsed } from "@/lib/sidebarCollapse";

function ProtectedRoute({ children, user, setUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [needsTenantSelect, setNeedsTenantSelect] = useState(false);

  useEffect(() => {
    // Always fetch full user data from /auth/me for complété fields (is_superadmin, tenant_role, modules)
    const quickUser = location.state?.user;
    if (quickUser) {
      setUser(quickUser);
      setIsAuthenticated(true);
    }
    authAPI.me()
      .then((res) => {
        setUser(res.data);
        setIsAuthenticated(true);
        // Apply branding CSS variables if marque_blanche is active
        const b = res.data.branding;
        if (b && Object.keys(b).length > 0) {
          const root = document.documentElement;
          if (b.primary_color) root.style.setProperty("--brand-primary", b.primary_color);
          if (b.accent_color) root.style.setProperty("--brand-accent", b.accent_color);
          if (b.sidebar_color) root.style.setProperty("--brand-sidebar", b.sidebar_color);
        }
        // Check if tenant is selected (auto-select if only 1)
        if (!res.data.current_tenant_id && res.data.tenants?.length > 1) {
          setNeedsTenantSelect(true);
        }
      })
      .catch(() => { if (!quickUser) { setIsAuthenticated(false); navigate("/login", { replace: true }); } });
  }, [location.state, navigate, setUser]);

  const [collapsed] = useSidebarCollapsed();

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }
  if (isAuthenticated === false) return null;
  if (needsTenantSelect && location.pathname !== "/select-tenant") {
    return <Navigate to="/select-tenant" replace />;
  }

  const handleTenantSwitch = () => {
    window.location.href = "/";
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar user={user} onTenantSwitch={handleTenantSwitch} />
      <QuickCreateButton user={user} />
      {user?.session_idle_timeout_minutes ? <IdleSessionManager timeoutMinutes={user.session_idle_timeout_minutes} /> : null}
      {(user?.modules || []).includes("chatbot") && <AIChatWidget />}
      <main className={`${collapsed ? "lg:ml-16" : "lg:ml-64"} pt-14 lg:pt-0 min-h-screen transition-[margin] duration-200`} data-testid="main-content">
        {children}
      </main>
    </div>
  );
}

function AppRouter() {
  const [user, setUser] = useState(null);
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<ProtectedRoute user={user} setUser={setUser}><DashboardPage user={user} /></ProtectedRoute>} />
      <Route path="/produits" element={<ProtectedRoute user={user} setUser={setUser}><ProductsPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute user={user} setUser={setUser}><ClientsPage user={user} /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute user={user} setUser={setUser}><ClientDetailPage user={user} /></ProtectedRoute>} />
      <Route path="/fournisseurs" element={<ProtectedRoute user={user} setUser={setUser}><SuppliersPage /></ProtectedRoute>} />
      <Route path="/fournisseurs/commandes" element={<ProtectedRoute user={user} setUser={setUser}><SupplierOrdersPage /></ProtectedRoute>} />
      <Route path="/fournisseurs/devis-pdf" element={<ProtectedRoute user={user} setUser={setUser}><SupplierQuotesPage /></ProtectedRoute>} />
      <Route path="/fournisseurs/:id" element={<ProtectedRoute user={user} setUser={setUser}><SupplierDetailPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute user={user} setUser={setUser}><DocumentsPage user={user} /></ProtectedRoute>} />
      <Route path="/statistiques" element={<ProtectedRoute user={user} setUser={setUser}><StatsPage /></ProtectedRoute>} />
      <Route path="/tva" element={<ProtectedRoute user={user} setUser={setUser}><TvaPage /></ProtectedRoute>} />
      <Route path="/factures-fournisseurs" element={<ProtectedRoute user={user} setUser={setUser}><SupplierInvoicesPage /></ProtectedRoute>} />
      <Route path="/marges-produits" element={<ProtectedRoute user={user} setUser={setUser}><MarginAnalysisPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute user={user} setUser={setUser}><AdminPage user={user} /></ProtectedRoute>} />
      <Route path="/facturation" element={<ProtectedRoute user={user} setUser={setUser}><BillingForecastPage user={user} /></ProtectedRoute>} />
      <Route path="/relevé-bancaire" element={<ProtectedRoute user={user} setUser={setUser}><BankStatementPage /></ProtectedRoute>} />
      <Route path="/bank-statement" element={<ProtectedRoute user={user} setUser={setUser}><BankStatementPage /></ProtectedRoute>} />
      <Route path="/campagnes" element={<ProtectedRoute user={user} setUser={setUser}><CampaignsPage /></ProtectedRoute>} />
      <Route path="/marketing/campagnes" element={<ProtectedRoute user={user} setUser={setUser}><CampaignsPage /></ProtectedRoute>} />
      <Route path="/marketing/listes" element={<ProtectedRoute user={user} setUser={setUser}><MailingListsPage /></ProtectedRoute>} />
      <Route path="/marketing/templates" element={<ProtectedRoute user={user} setUser={setUser}><EmailTemplatesPage /></ProtectedRoute>} />
      <Route path="/exports" element={<ProtectedRoute user={user} setUser={setUser}><ExportPage /></ProtectedRoute>} />
      <Route path="/comptabilite" element={<ProtectedRoute user={user} setUser={setUser}><ComptabilitePage /></ProtectedRoute>} />
      <Route path="/notes-de-frais" element={<ProtectedRoute user={user} setUser={setUser}><ExpenseNotesPage /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute user={user} setUser={setUser}><ImportPage /></ProtectedRoute>} />
      <Route path="/select-tenant" element={<TenantSelectPage user={user} setUser={setUser} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="bottom-right" richColors />
    </BrowserRouter>
  );
}

export default App;
